// frontend/src/components/paywall/FAQSection.tsx

import React, { useState } from 'react';

/**
 * FAQ item interface
 */
interface FAQItem {
  question: string;
  answer: string;
}

/**
 * Common questions about the paywall and Pro subscription
 */
const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'What counts as 1 AI help?',
    answer:
      'Each AI interaction (solution verification, tutor chat message, collection generation, etc.) uses 1 help. System features like browsing problems and collections are always free.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes! Cancel anytime and keep access until your billing period ends. No hidden fees or cancellation charges.',
  },
  {
    question: 'What happens if I don\'t upgrade?',
    answer:
      'You\'ll keep access to all non-AI features including browsing problems, collections, and basic problem details. Only AI-powered tools require credits.',
  },
  {
    question: 'Do unused credits roll over?',
    answer:
      'No, credits reset at the start of each billing cycle. However, 10,000 credits per month is designed to support active daily practice.',
  },
  {
    question: 'What if there\'s a system error?',
    answer:
      'You\'re never charged for system failures. If an AI request fails on our end, your credit is automatically refunded.',
  },
];

/**
 * FAQ Section Component
 * Displays collapsible frequently asked questions
 */
export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  /**
   * Toggle FAQ item open/closed
   */
  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  /**
   * Chevron icon for expand/collapse indicator
   */
  const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
    <svg
      className={`faq-chevron ${isOpen ? 'open' : ''}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M7 10l5 5 5-5z"
        fill="currentColor"
      />
    </svg>
  );

  return (
    <div className="faq-section">
      <h3 className="faq-title">Common Questions</h3>

      <div className="faq-list">
        {FAQ_ITEMS.map((item, index) => (
          <div key={index} className="faq-item">
            <button
              className="faq-question"
              onClick={() => toggleItem(index)}
              aria-expanded={openIndex === index}
              aria-controls={`faq-answer-${index}`}
            >
              <span>{item.question}</span>
              <ChevronIcon isOpen={openIndex === index} />
            </button>

            <div
              id={`faq-answer-${index}`}
              className={`faq-answer ${openIndex === index ? 'open' : ''}`}
              aria-hidden={openIndex !== index}
            >
              {item.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
